'use client';

import { IImageListItem, TImageMetaPositionAlt } from '@northladder/apis';
import { useRegionStore } from '@northladder/services';

import { apiTranslations } from '../../../translations';

import {
  DoneUploadingV2,
  PendingReUpload,
  PendingUploadingV2,
} from '../../components';
import { useRouteURL } from '../../hooks';

interface IDeviceImagesCaptureAndUploadProps {
  /**
   * List of already uploaded device images
   */
  completedImages: IImageListItem[];
  /**
   * List of device images pending upload
   */
  pendingImages: IImageListItem[];
  /**
   * List of device images pending re-upload
   */
  pendingReuploadImages: IImageListItem[];
  /**
   * List of ids of the image, retrieved from the response images list.
   */
  imagesDataIds: string[];
}

/**
 * -----------------------------------------------------------------------------
 * Creates functionality for selecting, uploading and previewing the uploaded
 * image.
 * - This highlights the images pending re-upload, then those that are pending
 * and finally the ones that have already been uploaded.
 */
export function DeviceImagesCaptureAndUploadV2({
  completedImages,
  pendingImages,
  pendingReuploadImages,
  imagesDataIds,
}: IDeviceImagesCaptureAndUploadProps) {
  const { assessmentType } = useRouteURL();
  const cdnBaseUrl =
    useRegionStore()?.tenantConfig?.cdnBaseUrl ||
    process.env.NEXT_PUBLIC_DIAGNOSTICS_CDN_BASE_URL;

  const itemsCount = imagesDataIds.length;
  const rowItemsClass = `lg:grid-cols-${itemsCount}`;

  return (
    <section
      className={`
        grid grid-cols-1 flex-wrap gap-2 py-2 sm:grid-cols-2 sm:gap-x-2
        md:grid-cols-3 ${rowItemsClass}
      `}
    >
      {assessmentType === 'GRADE_PRO' &&
        pendingReuploadImages.map((image) => {
          const { url, metadata, uploadedOn, imageRetakeReasons, comment } =
            image;
          const { color, position } = metadata;

          const imagePath = `${cdnBaseUrl}${url}`;
          const title = apiTranslations.positions[position];

          return (
            <PendingReUpload
              key={color + position}
              imagePath={imagePath}
              position={position as TImageMetaPositionAlt}
              retakeComment={comment}
              retakeReasons={imageRetakeReasons}
              title={title}
              uploadedOn={uploadedOn}
            />
          );
        })}

      {pendingImages.map((image) => {
        const { color, position } = image.metadata;
        const title = apiTranslations.positions[position];
        const titlePostfix = apiTranslations.colors[color];

        return (
          <PendingUploadingV2
            key={color + position}
            imageId={color}
            position={position as TImageMetaPositionAlt}
            title={title}
            titlePostfix={titlePostfix}
          />
        );
      })}

      {completedImages.map((image) => {
        const { url, metadata, uploadedOn } = image;
        const { color, position } = metadata;

        // Override the `APPLE` flow to only show the placeholder image instead
        // of the actual image from the API fileUploadPath.
        let imagePath = '';

        if (assessmentType === 'GRADE_PRO' && url) {
          imagePath = `${cdnBaseUrl}${url}`;
        }

        const title = apiTranslations.positions[position];
        const titlePostfix = apiTranslations.colors[color];

        const imageId = assessmentType === 'GRADE_PRO' ? position : color;

        return (
          <DoneUploadingV2
            key={color + position}
            imageId={imageId}
            imagePath={imagePath}
            title={title}
            titlePostfix={titlePostfix}
            uploadedOn={uploadedOn}
          />
        );
      })}
    </section>
  );
}
